/** Fábricas y utilidades sobre figuras: crear, medir, mover, escalar. */
import type {
  DrawShape,
  GeoKind,
  GeoShape,
  ImageShape,
  NoteShape,
  Shape,
  TextShape,
} from '../../shared/types.ts'
import type { Box } from '../lib/geom.ts'
import { rotatedBounds } from '../lib/geom.ts'

let counter = 0

export function newId(prefix = 's'): string {
  counter = (counter + 1) % 100000
  return `${prefix}_${Date.now().toString(36)}${counter.toString(36)}${Math.random().toString(36).slice(2, 7)}`
}

export const NOTE_DEFAULT_SIZE = 200
export const NOTE_PADDING = 16

export function createDraw(params: {
  points: number[]
  x: number
  y: number
  w: number
  h: number
  color: string
  size: number
  highlighter?: boolean
  createdBy: string
  z: number
}): DrawShape {
  return {
    id: newId('d'),
    type: 'draw',
    x: params.x,
    y: params.y,
    w: params.w,
    h: params.h,
    baseW: params.w,
    baseH: params.h,
    rotation: 0,
    z: params.z,
    createdBy: params.createdBy,
    updatedAt: Date.now(),
    points: params.points,
    color: params.color,
    size: params.size,
    highlighter: params.highlighter,
  }
}

export function createNote(params: {
  x: number
  y: number
  color: string
  createdBy: string
  author?: string
  z: number
  size?: number
}): NoteShape {
  const size = params.size ?? NOTE_DEFAULT_SIZE
  return {
    id: newId('n'),
    type: 'note',
    x: params.x,
    y: params.y,
    w: size,
    h: size,
    rotation: 0,
    z: params.z,
    createdBy: params.createdBy,
    updatedAt: Date.now(),
    color: params.color,
    text: '',
    fontSize: 24,
    align: 'center',
    author: params.author,
  }
}

export function createText(params: {
  x: number
  y: number
  color: string
  fontSize: number
  createdBy: string
  z: number
}): TextShape {
  return {
    id: newId('t'),
    type: 'text',
    x: params.x,
    y: params.y,
    w: 320,
    h: params.fontSize * 1.4,
    rotation: 0,
    z: params.z,
    createdBy: params.createdBy,
    updatedAt: Date.now(),
    text: '',
    color: params.color,
    fontSize: params.fontSize,
    align: 'left',
  }
}

export function createGeo(params: {
  kind: GeoKind
  x: number
  y: number
  w: number
  h: number
  color: string
  fill: string
  strokeWidth: number
  createdBy: string
  z: number
}): GeoShape {
  return {
    id: newId('g'),
    type: 'shape',
    kind: params.kind,
    x: params.x,
    y: params.y,
    w: params.w,
    h: params.h,
    rotation: 0,
    z: params.z,
    createdBy: params.createdBy,
    updatedAt: Date.now(),
    color: params.color,
    fill: params.fill,
    strokeWidth: params.strokeWidth,
    text: '',
    fontSize: 20,
  }
}

export function createImage(params: {
  src: string
  x: number
  y: number
  w: number
  h: number
  naturalW: number
  naturalH: number
  createdBy: string
  z: number
}): ImageShape {
  return {
    id: newId('i'),
    type: 'image',
    x: params.x,
    y: params.y,
    w: params.w,
    h: params.h,
    rotation: 0,
    z: params.z,
    createdBy: params.createdBy,
    updatedAt: Date.now(),
    src: params.src,
    naturalW: params.naturalW,
    naturalH: params.naturalH,
  }
}

export function shapeBox(shape: Shape): Box {
  return { x: shape.x, y: shape.y, w: shape.w, h: shape.h }
}

/** Bounding box alineado a ejes, incluyendo rotación y el grosor del trazo. */
export function shapeBounds(shape: Shape): Box {
  const base = rotatedBounds(shapeBox(shape), shape.rotation)
  const pad = shape.type === 'draw' ? shape.size / 2 + 2 : shape.type === 'shape' ? shape.strokeWidth : 0
  return { x: base.x - pad, y: base.y - pad, w: base.w + pad * 2, h: base.h + pad * 2 }
}

export function moveShape<T extends Shape>(shape: T, dx: number, dy: number): T {
  return { ...shape, x: shape.x + dx, y: shape.y + dy, updatedAt: Date.now() }
}

export function isTextual(shape: Shape): shape is NoteShape | TextShape | GeoShape {
  return shape.type === 'note' || shape.type === 'text' || shape.type === 'shape'
}

export function shapeText(shape: Shape): string {
  if (shape.type === 'note' || shape.type === 'text') return shape.text
  if (shape.type === 'shape') return shape.text ?? ''
  return ''
}

/** Duplica figuras con un desplazamiento, generando ids nuevos. */
export function duplicateShapes(shapes: Shape[], dx: number, dy: number, startZ: number): Shape[] {
  return shapes.map((shape, i) => ({
    ...shape,
    id: newId(shape.type[0]),
    x: shape.x + dx,
    y: shape.y + dy,
    z: startZ + i + 1,
    updatedAt: Date.now(),
  }))
}
